/**
 * deriveCategoryFilteredView
 *
 * Given:
 *   - the full per-element AIM register (`baseData[]` — what the
 *     ResultsPage table normally renders), and
 *   - the per-rule validation_results rows (`details[]` — from
 *     `/api/audits/{id}/validation-details`, one row per
 *     element × rule),
 *   - a selected `category` (snake_case key matching the
 *     `category_breakdown[i].category` value),
 *
 * return a NEW `{data, summary}` pair scoped to that category so the
 * Recharts dashboard can re-render against the category subset without
 * any backend round-trip.
 *
 * Per-element status is derived from the worst rule outcome in the
 * category: FAIL > WARN > REVIEW > PASS. Confidence is the simple ratio
 * (passed_in_category / total_in_category) × 100 — transparent so a
 * reviewer can sanity-check it at a glance.
 *
 * Returns null when filtering would produce an empty view (so callers
 * can fall back to the unfiltered data + show "no checks in this
 * category for any element").
 */
const STATUS_RANK = { FAIL: 4, WARN: 3, REVIEW: 2, PASS: 1 };

function _worst(a, b) {
  // Unknown statuses (anything we haven't explicitly classified —
  // e.g. a future 'INFO' bucket or a legacy 'SKIPPED' row leaking
  // through) rank at REVIEW level so they surface in the chart
  // rather than getting silently dropped from the worst-status
  // rollup. The interactive dashboard should never hide rows we
  // didn't recognise — that's the kind of regression that would
  // make a reviewer trust numbers that quietly omit data.
  const ra = STATUS_RANK[a] || STATUS_RANK.REVIEW;
  const rb = STATUS_RANK[b] || STATUS_RANK.REVIEW;
  return rb > ra ? b : a;
}

export function deriveCategoryFilteredView(baseData, details, category) {
  if (!category) {
    return null;  // caller uses unfiltered baseData
  }
  const baseByEl = new Map();
  for (const row of (baseData || [])) {
    baseByEl.set(row.elementId, row);
  }
  // Group filtered details by element_id.
  const grouped = new Map();
  for (const d of (details || [])) {
    if (d.category !== category) continue;
    if (!d.element_id) continue;
    const arr = grouped.get(d.element_id) || [];
    arr.push(d);
    grouped.set(d.element_id, arr);
  }
  if (grouped.size === 0) {
    return { data: [], summary: { avg_confidence: 0 } };
  }
  const filtered = [];
  let confSum = 0;
  for (const [elId, rows] of grouped.entries()) {
    let worst = 'PASS';
    let passed = 0, warns = 0, fails = 0;
    const issues = [];
    for (const r of rows) {
      const s = (r.status || 'REVIEW').toUpperCase();
      worst = _worst(worst, s);
      if (s === 'PASS') passed += 1;
      else if (s === 'WARN') warns += 1;
      else if (s === 'FAIL') {
        fails += 1;
        if (r.issue) issues.push(r.issue);
      }
    }
    const total = rows.length;
    const conf = total > 0 ? Math.round((passed / total) * 100) : 0;
    confSum += conf;
    const base = baseByEl.get(elId) || {};
    filtered.push({
      elementId: elId,
      assetName: base.assetName || rows[0].element_name || elId,
      ifcClass: base.ifcClass || rows[0].ifc_class || '',
      objectType: base.objectType || '',
      storey: base.storey || '',
      confidenceScore: conf,
      // Category-scoped certification view: an element is self-certified
      // in this category only if it had no FAILs and no WARNs here.
      status: (fails === 0 && warns === 0) ? 'SELF_CERTIFIED' : 'HITL_FLAGGED',
      overallStatus: worst,
      totalChecks: total,
      passed,
      warnings: warns,
      failures: fails,
      issues,
    });
  }
  const avg = filtered.length > 0 ? Math.round(confSum / filtered.length) : 0;
  return {
    data: filtered,
    summary: { avg_confidence: avg, total_elements: filtered.length },
  };
}
